import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Subject, of } from 'rxjs'
import { catchError, distinctUntilChanged, flatMap, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators'
import { fadeInOnly } from '../../../animations/fadeInOnly'
import { User } from '@overtur-monorepo/keying-user'
import { ChamberTypes } from '../../../constants/chamber-types'
import { KeySystemStatuses } from '../../../constants/keySystem-statuses'
import { KeySystem, Structure, StructureDetails } from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { KeyingAuthService } from '../../../services/keying.auth.service'
import { KeyingDataSets, KeyingDataSetsService } from '../../../services/keying.data.sets.service'
import { KeyingStructureService } from '../../../services/keying.structure.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { LoadingService } from '../../../services/loading.service'
import { DialogService } from '../../../services/dialog.service'
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component'

@Component({
    selector: 'app-structure-card',
    templateUrl: './structure-card.component.html',
    styleUrls: ['./structure-card.component.scss'],
    animations: [fadeInOnly],
    standalone: false
})
export class StructureCardComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  chamberTypes = ChamberTypes

  users: User[]
  usersNamesList = []
  lockedById
  lockedBy

  structure: Structure
  keySystem: KeySystem
  keyingDataSets: KeyingDataSets
  keywayFamily
  combinatingRule
  hasDocuments

  managedBy = 'KeyCom'

  keySystemStatus = ''
  isReadOnly = true

  editStructureForm: FormGroup
  editing = false
  expanded = true
  countries
  states
  disableUserChange = false

  focus = {
    name: false,
    country: false,
    state: false,
    city: false,
  }

  public errorMessages = {
    name: [{ type: 'required', message: 'A Name is required.' }],
    country: [{ type: 'required', message: 'A Country is required.' }],
    state: [{ type: 'required', message: 'A State is required.' }],
    city: [{ type: 'required', message: 'A City is required.' }],
  }

  constructor(
    private keyingSystemService: KeyingSystemService,
    private keyingStructureService: KeyingStructureService,
    private keyingDataSetsService: KeyingDataSetsService,
    private keyingAuthService: KeyingAuthService,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    public form: FormBuilder,
    private dialogService: DialogService
  ) {
    this.editStructureForm = this.form.group({
      city: new FormControl(null, [Validators.required]),
      country: new FormControl(null, [Validators.required]),
      state: new FormControl(null, [Validators.required]),
      name: new FormControl(null, [Validators.required]),
      hasDocuments: new FormControl(null),
    })
  }

  /* istanbul ignore next */
  ngOnInit(): void {
    this.keyingAuthService.users$.pipe(takeUntil(this.destroy$)).subscribe((users: User[]) => {
      if (!users) {
        this.keyingAuthService.getUsers()
        return
      }
      this.users = users
      this.users.forEach((user) => {
        this.usersNamesList.push({ name: user.firstName + ' ' + user.lastName, id: user.id })
      })
      this.usersNamesList.sort((a, b) => a.name.localeCompare(b.name))
    })

    this.editStructureForm.controls.country.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((country) => {
      this.states = country?.states ?? []

      if (this.states.length > 0) {
        this.editStructureForm.controls.state.setValue(this.states.find((s) => s.code === this.structure.stateCode))
        this.editStructureForm.controls.state.setValidators([Validators.required])
      } else {
        this.editStructureForm.controls.state.setValue(null)
        this.editStructureForm.controls.state.setValidators([])
      }

      this.editStructureForm.controls.state.updateValueAndValidity()
    })
    this.keyingStructureService.structure$
      .pipe(
        takeUntil(this.destroy$),
        flatMap((structure: Structure) => {
          this.structure = structure
          return this.keyingSystemService.keySystem$
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (keySystem: KeySystem) => {
          this.keySystem = keySystem

          this.keyingDataSetsService.keyingDataSets$
            .pipe(takeUntil(this.destroy$))
            .subscribe((keyingDataSets: KeyingDataSets) => {
              if (!keyingDataSets) {
                return
              }
              this.keyingDataSets = keyingDataSets
              this.keywayFamily = this.keyingDataSets.keywayFamilies.find(
                (kwf) => kwf.id === this.structure?.keywayFamilyId
              )
              this.combinatingRule = this.keyingDataSets.combinatingRuleSets.find(
                (kwf) => kwf.id === this.structure?.combinatingRuleSetId
              )
              this.getKeySystemStatus()
              this.setForm()
            })
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
    this.getlockedBy()

    this.keyingDataSetsService.structureDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((details: StructureDetails) => {
        this.isReadOnly =
          details?.mecsInProgress || details?.publishingInProgress || details?.pinningGenerationInprogress
      })
    this.hasDocuments = this.structure?.hasDocuments

    this.keyingStructureService.structure$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((a, b) => a?.id === b?.id),
        switchMap((structure: Structure) => {
          if (!structure) return of(null)
          return this.keyingStructureService.getStructureOnboardStatus(structure.id)
        })
      )
      .subscribe((isOnBoarded: boolean | null) => {
        if (isOnBoarded === null) return
        this.managedBy = isOnBoarded ? 'OKSM' : 'KeyCom'
      })
  }

  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges) {
    if (changes?.selectedCylinder?.currentValue) {
      // this.editStructureForm.reset()
    }
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  getlockedBy() {
    this.keyingSystemService.keySystem$.subscribe((keySystem) => {
      if (!keySystem) {
        this.keyingSystemService.getKeySystem(this.structure.id, this.keySystem.id)
      }
      this.lockedBy = this.usersNamesList.filter((user) => user.id === keySystem.lockedById)
    })
  }

  /* istanbul ignore next */
  private getKeySystemStatus(): void {
    this.keyingSystemService.keySystem$.subscribe((keySystem) => {
      const statusId = keySystem?.statusId
      if (statusId) {
        Object.keys(KeySystemStatuses).forEach((x) => {
          if (x == statusId.toString()) {
            this.keySystemStatus = KeySystemStatuses[x]
          }
        })
      }
    })
    if (this.keySystemStatus === 'MECS In Progress' || this.keySystemStatus === 'In MECS Queue') {
      this.disableUserChange = true
    } else {
      this.disableUserChange = false
    }
  }

  /* istanbul ignore next */
  hasDocs() {
    this.hasDocuments = !this.hasDocuments
  }

  /* istanbul ignore next */
  setForm() {
    this.editStructureForm.controls.city.setValue(this.structure.city)
    this.editStructureForm.controls.name.setValue(this.structure.name)
    this.editStructureForm.controls.hasDocuments.setValue(this.hasDocuments)

    this.keyingDataSetsService.keyingDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((keyingDataSets: KeyingDataSets) => {
        if (!keyingDataSets) {
          return
        }

        this.countries = keyingDataSets.countries
        const selectedCountry = this.countries.find((c) => c.code === this.structure.countryCode)
        this.editStructureForm.controls.country.setValue(selectedCountry)

        this.states = selectedCountry.states
        this.editStructureForm.controls.state.setValue(this.states.find((s) => s.code === this.structure.stateCode))
      })
  }

  /* istanbul ignore next */
  saveStructure() {
    const controls = this.editStructureForm.controls

    const request = [
      {
        op: 'replace',
        path: '/Name',
        value: controls.name.value,
      },
      {
        op: 'replace',
        path: '/Address',
        value: {
          city: controls.city.value,
          stateCode: controls.state.value?.code,
          countryCode: controls.country.value.code,
        },
      },
      {
        op: 'replace',
        path: '/HasDocuments',
        value: controls.hasDocuments.value,
      },
    ]

    this.keyingStructureService
      .patchStructure(this.structure.id, request)
      .pipe(take(1))
      .subscribe(
        (structure) => {
          this.keyingStructureService.selectStructure(structure)
          this.editing = false
          this.errorService.confirm('Updated Structure')
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  chamberType(chamberTypeId) {
    return this.chamberTypes[chamberTypeId]
  }

  /* istanbul ignore next */
  toggleEdit() {
    this.setForm()
    this.editing = !this.editing
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  changeUser(structure, userId) {
    const request = [
      {
        op: 'replace',
        path: '/lockedById',
        value: userId,
      },
    ]
      //14 is the ID for In Design Systems
      if(this.keySystem.statusId === 14) {
      const confirmData = {
        message: `Are you sure you want to assign and put this structure into an "In Progress" status?`,
        showCancel: true,
        cancel: 'Cancel',
        confirm: 'Confirm',
      }
      const dialog = this.dialogService.open(ConfirmDialogComponent, confirmData, false)
      dialog
        .afterClosed().subscribe(((result) => {
            if (result.confirmed) {
              this.loadingService.set({ isLoading: true, message: 'Reassigning Structure' })
              this.patchStructure(structure, request)
            } else {
              this.loadingService.set({ isLoading: false, message: '' })
              // return of(null)
            }
          })
        )
    } else {
      this.loadingService.set({ isLoading: true, message: 'Reassigning Structure' })
      this.patchStructure(structure, request)
      }
    
  }

  /* istanbul ignore next */
  patchStructure(structure, request) {
    this.keyingStructureService
    .patchStructure(structure.id, request)
    .pipe(take(1))
    .subscribe(
      () => {
        this.errorService.confirm('Updated Structure')
        this.loadingService.set({ isLoading: false, message: '' })
        this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
      },
      (error) => {
        this.loadingService.set({ isLoading: false, message: '' })
        this.errorService.throw(error)
      }
    )
  }

  /* istanbul ignore next */
  toggleDealerSpec(event) {
    const confirmData = {
      message: `Are you sure you would like to convert this key system to a Dealer Specified key system? Once confirmed, this action cannot be undone.`,
      showCancel: true,
      cancel: 'Cancel',
      confirm: 'Confirm',
    }
    const dialog = this.dialogService.open(ConfirmDialogComponent, confirmData, false)

    dialog
      .afterClosed()
      .pipe(
        take(1),
        switchMap((result) => {
          if (result.confirmed) {
            this.loadingService.set({ isLoading: true, message: 'Converting Key System' })
            return this.keyingSystemService.convertSystemToDealerSpec(this.structure.id, this.keySystem.id).pipe(
              mergeMap((res) => {
                this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
                this.loadingService.set({ isLoading: false, message: '' })

                this.errorService.confirm('System has successfully been converted to Dealer Specified.')
                return of(null)
              }),
              catchError((error) => {
                this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
                this.loadingService.set({ isLoading: false, message: '' })
                this.errorService.throw(error)
                return of(null)
              })
            )
          } else {
            event.source.checked = false
            return of(null)
          }
        })
      )
      .subscribe()
  }
}
